import { useState } from 'react'
import { Card, Button, Badge } from '@/components/common'
import { Modal } from '@/components/common'

export interface Subscription {
  id: string
  serviceName: 'BLIS' | 'IZG'
  planTier: string
  status: 'pending' | 'active' | 'trial' | 'expired' | 'cancelled'
  currentPeriodStart: string
  currentPeriodEnd: string
  paymentGateway: 'stripe' | 'moyasar' | 'sadad'
  cancelAtPeriodEnd?: boolean
  price: {
    amount: number
    currency: string
    interval: 'monthly' | 'annual'
  }
}

interface SubscriptionCardProps {
  subscription: Subscription
  onManage?: (id: string) => void
  onCancel?: (id: string) => void
  onReactivate?: (id: string) => void
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onManage,
  onCancel,
  onReactivate,
}) => {
  const [showDetails, setShowDetails] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const statusColors = {
    pending: 'warning',
    active: 'success',
    trial: 'default',
    expired: 'danger',
    cancelled: 'warning',
  } as const

  const statusLabels = {
    pending: 'Pending Approval',
    active: 'Active',
    trial: 'Trial',
    expired: 'Expired',
    cancelled: 'Cancelled',
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel(subscription.id)
      setShowCancelConfirm(false)
    }
  }

  return (
    <>
      <Card className="hover:shadow-lg transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-text-main-light dark:text-text-main-dark mb-1">
              {subscription.serviceName}
            </h3>
            <p className="text-text-sub-light dark:text-text-sub-dark">{subscription.planTier}</p>
          </div>
          <Badge variant={statusColors[subscription.status]}>
            {statusLabels[subscription.status]}
          </Badge>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-text-sub-light dark:text-text-sub-dark">Billing Period:</span>
            <span className="text-text-main-light dark:text-text-main-dark font-medium">
              {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-text-sub-light dark:text-text-sub-dark">Payment Method:</span>
            <span className="text-text-main-light dark:text-text-main-dark font-medium capitalize">
              {subscription.paymentGateway}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-text-sub-light dark:text-text-sub-dark">Price:</span>
            <span className="text-text-main-light dark:text-text-main-dark font-medium">
              {subscription.price.amount.toLocaleString()} {subscription.price.currency} /{' '}
              {subscription.price.interval === 'monthly' ? 'mo' : 'yr'}
            </span>
          </div>
        </div>

        {subscription.cancelAtPeriodEnd && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              ⚠️ This subscription will cancel at the end of the current billing period.
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowDetails(true)}
            className="flex-1"
          >
            View Details
          </Button>
          {subscription.status === 'active' && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowCancelConfirm(true)}
              className="flex-1"
            >
              Cancel
            </Button>
          )}
          {subscription.status === 'cancelled' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onReactivate && onReactivate(subscription.id)}
              className="flex-1"
            >
              Reactivate
            </Button>
          )}
          {subscription.status === 'expired' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onManage && onManage(subscription.id)}
              className="flex-1"
            >
              Renew
            </Button>
          )}
        </div>
      </Card>

      {/* Details Modal */}
      <Modal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        title={`${subscription.serviceName} Subscription Details`}
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">Service</p>
            <p className="font-semibold text-text-main-light dark:text-text-main-dark">
              {subscription.serviceName}
            </p>
          </div>
          <div>
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">Plan</p>
            <p className="font-semibold text-text-main-light dark:text-text-main-dark">
              {subscription.planTier}
            </p>
          </div>
          <div>
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">Status</p>
            <Badge variant={statusColors[subscription.status]}>
              {statusLabels[subscription.status]}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">
              Current Billing Period
            </p>
            <p className="text-text-main-light dark:text-text-main-dark">
              {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
            </p>
          </div>
          <div>
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">Payment Gateway</p>
            <p className="text-text-main-light dark:text-text-main-dark capitalize">
              {subscription.paymentGateway}
            </p>
          </div>
          <div>
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark mb-1">Price</p>
            <p className="text-text-main-light dark:text-text-main-dark">
              {subscription.price.amount.toLocaleString()} {subscription.price.currency} /{' '}
              {subscription.price.interval === 'monthly' ? 'month' : 'year'}
            </p>
          </div>
          {subscription.cancelAtPeriodEnd && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                This subscription is scheduled to cancel at the end of the current billing period on{' '}
                {formatDate(subscription.currentPeriodEnd)}.
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title="Cancel Subscription"
      >
        <div className="space-y-4">
          <p className="text-text-main-light dark:text-text-main-dark">
            Are you sure you want to cancel your {subscription.serviceName} {subscription.planTier}{' '}
            subscription?
          </p>
          <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
            Your subscription will remain active until {formatDate(subscription.currentPeriodEnd)}. You
            will continue to have access to all features until then.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setShowCancelConfirm(false)}>
              Keep Subscription
            </Button>
            <Button variant="danger" onClick={handleCancel}>
              Confirm Cancellation
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

